import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { setCspContent } from '../../../csp';
import styled from 'styled-components';
import { darken } from 'polished';
import { t } from 'ttag';
import { CTA } from "../../general/Button";
import powered_by from "../../../assets/images/OrderswiftLogo2025.png";

type GenericErrorPageProps = {
    title: string;
    message: string;
    buttonText: string;
    reload?: boolean;
};

const GenericErrorPage = ({ title, message, buttonText, reload = false }: GenericErrorPageProps) => {
    const navigate = useNavigate();

    const navigateBack = () => {
        navigate(-1);
    };

    const navigateToChannelSelectionPage = () => {
        navigate('/');
    };

    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', '#FFFFFF');
    document.querySelector('meta[name="icon"]')?.setAttribute('href', '/favicon.png');

    return (
        <Wrapper>
            <Helmet>
                <title>{title} - {t`Online Ordering - Powered by Orderswift`}</title>

                <link rel="icon" type="image/png" href="/favicon.png" />
                <link href="https://fonts.googleapis.com" rel="preconnect" />
                <link href="https://fonts.gstatic.com" rel="preconnect" crossOrigin="anonymous" />
                <link href="https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,100..1000;1,9..40,100..1000&amp;display=swap" rel="stylesheet" />

                <meta httpEquiv="Content-Security-Policy" content={setCspContent()} />
                <meta httpEquiv="Reporting-Endpoints" content='default="https://orderswift.report-uri.com/a/d/g"' />
                <meta httpEquiv="Report-To" content='{"group":"default","max_age":31536000,"endpoints":[{"url":"https://orderswift.report-uri.com/a/d/g"}],"include_subdomains":true}' />
                <meta httpEquiv="NEL" content='{"report_to":"default","max_age":31536000,"include_subdomains":true}' />
                <meta name="referrer" content="strict-origin-when-cross-origin" />
                <meta httpEquiv="Permissions-Policy" content="payment=(self), geolocation=(self)"/>

                <style>
                    {`
                        @font-face {
                            font-family: 'Gooper';
                            src: url('/gooper.woff2') format('woff2');
                            font-weight: normal;
                            font-style: normal;
                        }
                    `}
                </style>
            </Helmet>
            <MessageWrapper>
                <h1>{title}</h1>
                <p dangerouslySetInnerHTML={{__html: message}} />
                <CTA block onClick={reload ? navigateBack : navigateToChannelSelectionPage}>
                    <span>{buttonText}</span>
                </CTA>
            </MessageWrapper>
            <OrangeBowl>
                <svg width="183" height="177" viewBox="0 0 183 177" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M180.153 90.7871L10.895 0.522198C8.39783 -0.809556 5.16113 0.500412 3.73798 3.38023C-14.709 40.7788 -19.9951 84.0058 10.9382 117.807C11.5631 118.487 11.5157 119.645 10.8091 120.398L0.595129 131.337C-1.96928 134.082 -1.58574 138.446 1.36034 140.017L69.6309 176.425C72.577 177.997 76.4057 175.887 77.2623 172.231L80.6889 157.567C80.9228 156.569 81.8524 155.882 82.7631 156.014C127.821 162.67 160.988 134.402 181.762 98.32C183.368 95.5306 182.648 92.1102 180.151 90.7785L180.153 90.7871Z" fill="#FF6E06"/>
                </svg>
            </OrangeBowl>
            <BlueBowl>
                <svg width="147" height="167" viewBox="0 0 147 167" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M170.4 44.0508C173.495 41.2743 173.769 36.4951 170.803 33.5811C134.033 -2.53911 74.0354 -14.9563 30.8622 23.7699C-12.311 62.4962 -6.46319 123.486 25.4634 163.95C28.0391 167.215 32.8198 167.46 35.9152 164.683L170.4 44.0508Z" fill="#4C79FF"/>
                </svg>
            </BlueBowl>
            <div className="orderswift-banner">
                <img width={169} height={15} src={powered_by} alt={'Powered by Orderswift'} />
            </div>
        </Wrapper>
    );
};

const Wrapper = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    .orderswift-banner {
        z-index: 999;
        display: flex;
        justify-content: center;
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        background: #FFFFFF;
        border-top: 2px solid #000000;
        padding: 10px 0;
    }
`;

const OrangeBowl = styled.div`
    position: fixed;
    top: 25%;
    left: 0;
`;

const BlueBowl = styled.div`
    position: fixed;
    bottom: 25%;
    right: 0;
`;

const MessageWrapper = styled.div`
    width: 100%;
    max-width: 480px;
    padding: 20px;
    text-align: center;
    z-index: 999;

    h1 {
        margin: 0;
        padding: 0;
        margin-bottom: 16px;
        font-family: 'Gooper', serif;
        font-size: 40px;
        line-height: 50x;
        color: #000000;
    }

    p {
        font-family: 'DM Sans', sans-serif;
        font-size: 20px;
        line-height: 28px;
        color: #000000;
    }

    button {
        margin-top: 24px;
        font-family: 'DM Sans', sans-serif;
        font-size: 20px;
        line-height: 20px;
        color: #000000;
        background: #9EF777;
        border: 1px solid #000000;
        border-radius: 8px;

        &:hover {
            background: ${darken(0.05, '#9EF777')};
        }

        &:active {
            background: ${darken(0.1, '#9EF777')};
        }
    }
`;

export default GenericErrorPage;
